// Named helpers around Api.request.
// Paths keep the legacy `departament` spelling to match the backend.
export function createEndpoints(api) {
  const json = (body) => ({
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  return {
    getReports(filters = {}) {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value != null && value !== '') params.set(key, value)
      })
      return api.request(`reports?${params}`)
    },
    getReport(id) { return api.request(`reports/${id}`) },
    getReportVersion(id, revision) { return api.request(`reports/${id}/versions/${revision}`) },
    checkReport(id, mode) {
      return api.request(`reports/${id}/check`, { method: 'POST', ...json({ mode }) })
    },
    confirmReport(id, attemptId, digest, action) {
      return api.request(`reports/${id}/regenerations/${attemptId}/confirm`, {
        method: 'POST', ...json({ digest, action }),
      })
    },
    dismissReportPreview(id, attemptId) {
      return api.request(`reports/${id}/regenerations/${attemptId}/dismiss`, { method: 'POST' })
    },
    getDepartaments({ includeEmployees = false } = {}) {
      const query = includeEmployees ? '?include=employees' : ''
      return api.request(`departaments${query}`)
    },

    getDepartament(id) {
      return api.request(`departament/${id}`)
    },

    createDepartament(payload) {
      return api.request('departament', {
        method: 'POST',
        ...json(payload),
      })
    },

    patchDepartament(id, payload) {
      return api.request(`departament/${id}`, {
        method: 'PATCH',
        ...json(payload),
      })
    },

    getDepartamentTypes() {
      return api.request('departament/types')
    },

    getGroup(id) {
      return api.request(`group/${id}`)
    },

    createGroup(payload) {
      return api.request('group', {
        method: 'POST',
        ...json(payload),
      })
    },

    getEmployee(id) {
      return api.request(`employee/${id}`)
    },

    createEmployee(payload) {
      return api.request('employee', {
        method: 'POST',
        ...json(payload),
      })
    },

    getUsers() {
      return api.request('users')
    },

    getRoles() {
      return api.request('roles')
    },

    getUserRoles(userId) {
      return api.request(`user/${userId}/roles`)
    },

    addUserRole(userId, payload) {
      return api.request(`user/${userId}/roles`, {
        method: 'POST',
        ...json(payload),
      })
    },

    removeUserRole(userId, roleId) {
      return api.request(`user/${userId}/roles`, {
        method: 'DELETE',
        ...json({ role_id: roleId }),
      })
    },

    getCalls({
      offset = 0,
      limit,
      from,
      to,
      day,
      tzOffsetMinutes,
      id,
      pipeline,
      departamentIds,
      groupIds,
    } = {}) {
      const params = new URLSearchParams()
      params.set('offset', String(offset))
      if (limit != null) {
        params.set('limit', String(limit))
      }
      if (pipeline) {
        params.set('pipeline', String(pipeline))
      }
      if (day) {
        params.set('day', String(day))
      }
      if (tzOffsetMinutes != null && tzOffsetMinutes !== '') {
        params.set('tzOffsetMinutes', String(tzOffsetMinutes))
      }
      if (from != null) {
        params.set('from', String(from))
      }
      if (to != null) {
        params.set('to', String(to))
      }
      if (id) {
        params.set('id', String(id))
      }
      departamentIds?.forEach((id) => params.append('departament_id', String(id)))
      groupIds?.forEach((id) => params.append('group_id', String(id)))
      return api.request(`calls?${params.toString()}`)
    },

    getCallsCalendar({ from, to, tzOffsetMinutes = new Date().getTimezoneOffset() } = {}) {
      const params = new URLSearchParams({
        from: String(from),
        to: String(to),
        tzOffsetMinutes: String(tzOffsetMinutes),
      })
      return api.request(`calls/calendar?${params.toString()}`)
    },

    getCallsStatus() {
      return api.request('calls/status')
    },

    getCallUploadUrls({
      departamentId,
      groupId,
      employeeId,
      targetNumber,
      cdrType,
      performantModel,
      fastModel,
      filenames = [],
    }) {
      const params = new URLSearchParams({
        departament_id: String(departamentId),
        group_id: String(groupId),
      })
      if (employeeId) {
        params.append('employee_id', String(employeeId))
      }
      if (targetNumber) {
        params.append('cc_target_number', targetNumber)
      }
      if (cdrType != null && String(cdrType).trim() !== '') {
        params.append('cc_cdr_type', String(cdrType).trim())
      }
      if (performantModel) {
        params.append('performant_model', performantModel)
      }
      if (fastModel) {
        params.append('fast_model', fastModel)
      }
      filenames.forEach((filename) => params.append('filenames', filename))

      // Errors are surfaced inline in the upload drawer, not as global toasts.
      return api.request(`calls/upload-url?${params.toString()}`, {
        skipClientErrorHandling: true,
        skipRateLimitHandling: true,
      })
    },

    savePrompt(payload) {
      return api.request('prompt', {
        method: 'POST',
        ...json(payload),
      })
    },

    deletePrompt({ lvl, id }) {
      return api.request('prompt/delete', {
        method: 'POST',
        ...json({ lvl, id }),
      })
    },

    togglePromptMark({ lvl, id, is_marked: isMarked }) {
      return api.request('prompt/toggle-mark', {
        method: 'POST',
        ...json({ lvl, id, is_marked: isMarked }),
      })
    },

    getCall(id) {
      return api.request(`calls/${id}`)
    },

    resetCallAttempts(id) {
      return api.request(`calls/${id}/resetAttempts`, { method: 'POST' })
    },

    archiveCall(id) {
      return api.request(`calls/${id}/archive`, { method: 'POST' })
    },

    restoreCall(id) {
      return api.request(`calls/${id}/restore`, { method: 'POST' })
    },

    getPromptDetail(lvl, id) {
      return api.request(`prompt/detail/${lvl}/${id}`)
    },

    updateCallsEmployee(payload) {
      return api.request('calls/updateEmployee', {
        method: 'POST',
        ...json(payload),
      })
    },

    changeCallsDate(payload) {
      return api.request('calls/changeDate', {
        method: 'POST',
        ...json(payload),
      })
    },

    massUpdateCalls(payload) {
      return api.request('calls/mass-update', {
        method: 'PATCH',
        ...json(payload),
      })
    },

    restartCalls(payload) {
      return api.request('calls/restart', {
        method: 'POST',
        ...json(payload),
      })
    },

    getModelsOptions() {
      return api.request('models/options')
    },

    getConfigurations() {
      return api.request('configurations')
    },

    getConfigurationAttachments(targetType, targetId, configurationId) {
      const suffix = configurationId != null ? `/${configurationId}` : ''
      return api.request(`configurations_attachments/${targetType}/${targetId}${suffix}`)
    },

    postConfigurationAttachment(payload) {
      return api.request('configurations_attachments', {
        method: 'POST',
        ...json(payload),
      })
    },

    deleteConfigurationAttachment(targetType, targetId, configurationId) {
      return api.request(
        `configurations_attachments/${targetType}/${targetId}/${configurationId}`,
        { method: 'DELETE' },
      )
    },

    getModelConfigurations() {
      return api.request('model-configurations')
    },

    patchModelConfiguration(key, payload) {
      return api.request(`model-configuration/${key}`, {
        method: 'PATCH',
        ...json(payload),
      })
    },

    deleteModelConfiguration(key) {
      return api.request(`model-configuration/${key}`, { method: 'DELETE' })
    },

    // filters: { offset, limit, status, sort, order, from, to, user_email } - empty values are skipped
    getClientReports(filters = {}) {
      const params = new URLSearchParams(
        Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && value !== ''),
      )
      return api.request(`client-reports?${params}`)
    },

    getClientReportReporters() {
      return api.request('client-reports/reporters')
    },

    setClientReportResolved(id, resolved) {
      return api.request(`client-reports/${id}`, {
        method: 'PATCH',
        ...json({ resolved }),
      })
    },
  }
}
